import React, { useMemo, useRef } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { Box } from '@chakra-ui/react'
import axios from 'axios'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  height?: string
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '내용을 입력하세요...',
  height = '300px'
}) => {
  const quillRef = useRef<ReactQuill>(null)

  // 이미지 업로드 핸들러
  const imageHandler = () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return

      // 이미지 크기 체크 (5MB 제한)
      if (file.size > 5 * 1024 * 1024) {
        alert('이미지 크기는 5MB 이하여야 합니다.')
        return
      }

      const formData = new FormData()
      formData.append('image', file)

      try {
        // 로딩 표시
        const quill = quillRef.current?.getEditor()
        if (quill) {
          const range = quill.getSelection()
          quill.insertText(range?.index || 0, '이미지 업로드 중...')
        }

        // 이미지 업로드 (axios는 자동으로 쿠키 전송)
        const response = await axios.post('/graph/inquiry-board/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true, // 쿠키 포함
        })

        if (response.data.success) {
          // 상대 경로 사용 (vite proxy가 처리)
          const imageUrl = response.data.url
          
          console.log('이미지 업로드 성공:', imageUrl)
          
          if (quill) {
            const range = quill.getSelection()
            const currentIndex = range?.index || 0
            
            // 로딩 텍스트 제거
            const textBefore = quill.getText(Math.max(0, currentIndex - 17), 17)
            if (textBefore.includes('이미지 업로드 중')) {
              quill.deleteText(currentIndex - 17, 17)
              // 이미지 삽입
              quill.insertEmbed(currentIndex - 17, 'image', imageUrl)
              // 커서를 이미지 다음으로 이동
              quill.setSelection(currentIndex - 16, 0)
            } else {
              // 로딩 텍스트가 없으면 현재 위치에 삽입
              quill.insertEmbed(currentIndex, 'image', imageUrl)
              quill.setSelection(currentIndex + 1, 0)
            }
          }
        }
      } catch (error: any) {
        console.error('이미지 업로드 오류:', error)
        console.error('오류 상세:', error.response?.data)
        
        // 로딩 텍스트 제거
        const quill = quillRef.current?.getEditor()
        if (quill) {
          const range = quill.getSelection()
          if (range && range.index > 17) {
            quill.deleteText(range.index - 17, 17)
          }
        }
        
        alert(`이미지 업로드에 실패했습니다.\n${error.response?.data?.message || error.message}`)
      }
    }
  }

  // Quill 모듈 설정
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    []
  )

  // Quill 포맷 설정
  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    'image',
  ]

  return (
    <Box
      sx={{
        '.quill': {
          borderRadius: 'md',
          border: '1px solid',
          borderColor: 'gray.200',
        },
        '.ql-container': {
          borderBottomLeftRadius: 'md',
          borderBottomRightRadius: 'md',
          minHeight: height,
        },
        '.ql-toolbar': {
          borderTopLeftRadius: 'md',
          borderTopRightRadius: 'md',
          background: 'gray.50',
        },
        '.ql-editor': {
          minHeight: height,
          fontSize: '14px',
        },
        '.ql-editor.ql-blank::before': {
          color: 'gray.400',
          fontStyle: 'normal',
        },
        '.ql-editor img': {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          margin: '10px 0',
        },
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </Box>
  )
}

export default RichTextEditor

